import React, { FC, useState } from 'react';
import { parseUsOrIso, formatTime, formatTimeRange } from 'core/utils/date';
import './savedShiftDisplay.scss';
import { DeleteIcon, DownChevron, OtherEditIcon as EditIcon, UpChevron } from '../../../Elements/CustomSVGIcon';

interface SavedDate {
    date: string
    startTime?: string | undefined;
    endTime?: string | undefined;
    startDate?: string | undefined;
    endDate?: string | undefined;
    range?: boolean;
}

interface SavedShiftProps {
    shiftName: string | undefined;
    savedDates: SavedDate[];
    onRemove: (key:string) => void;
    onEdit: (key:string) => void;
    // Retail general/access hides the end time (it defaults to the site's
    // open-day closing time), showing only the start time.
    hideEndTime?: boolean;
}

const SavedShift: FC<SavedShiftProps> = ({ shiftName, savedDates, onRemove, onEdit, hideEndTime = false }) => {
    const [showDates, setShowDates] = useState<boolean>(true);

    // Sort by date and startTimes
    const sortedDatedDates = savedDates.sort((a, b) => {
        if (a.date === b.date) {
            if (a.startTime && b.startTime) {
                return a.startTime.localeCompare(b.startTime);
            }
            return 0;
        }
        return a.date.localeCompare(b.date);
    });

    const handleShowDatesClick = () => {
        setShowDates(!showDates);
    };

    const getDateDisplay = (date: SavedDate) => {
        // date.date is MM-DD-YYYY; moment(date.date) with no format falls
        // back to the native parser and yields "Invalid date" in Safari, so
        // parse it explicitly via parseUsOrIso (Safari-safe).
        const formattedDate = parseUsOrIso(date.date).format('dddd Do MMMM');
        // Edit/Delete are always enabled here: this component only shows
        // draft (pre-submission) shifts, so a user must always be able to
        // change or remove a row regardless of whether the shift time has
        // already passed.
        const getTime = () => {
            // Retail general shows start-only; other flows show the full range.
            let timeLabel: string | null = null;
            if (hideEndTime) {
                timeLabel = date.startTime ? formatTime(date.startTime) : null;
            } else if (date.startTime && date.endTime) {
                timeLabel = formatTimeRange(date.startTime, date.endTime);
            }
            if (timeLabel) {
                return (
                    <><span className="label">{timeLabel}</span>
                        <div className="action-icons-wrapper">
                            <div onClick={() => onEdit(date.date)} className="icon-wrapper">
                                <EditIcon />
                            </div>
                            <div onClick={() => onRemove(date.date)} className="icon-wrapper">
                                <DeleteIcon />
                            </div>
                        </div>
                    </>
                );
            }
            return (
                <div className="action-icons-wrapper">
                    <div onClick={() => onEdit(date.date)} className="icon-wrapper">
                        <EditIcon />
                    </div>
                    <div onClick={() => onRemove(date.date)} className="icon-wrapper">
                        <DeleteIcon />
                    </div>
                </div>
            );
        };

        return (
            <div className="date-display" key={`${date.date}-${date.startTime}`}>
                <span className="label" style={{ width: '265px' }}>{formattedDate}</span>
                {getTime()}
            </div>
        );
    };

    return (
        <div className="saved-date-container">
            <div className="header">
                <h5 className="heading">{shiftName}</h5>
                <div className="icon-wrapper" onClick={handleShowDatesClick}>
                    {showDates ? <UpChevron /> : <DownChevron />}
                </div>
            </div>
            {showDates && <div className="dates-display-container">{sortedDatedDates.map((date) => getDateDisplay(date))}</div>}
        </div>
    );
};

export default SavedShift;
